// src/auth/forgot-password-page.tsx
//
// Route: /forgot-password. Flutter parity: forgot_password module — enter the
// account email, POST /api/Account/ForgotPassword, and on success move to the
// reset-via-access-code screen (the code is now in the user's inbox). Matches
// Flutter exactly: no existence pre-check (forgot_password_data_source_impl.dart
// posts the email directly). Email is validated with the SAME Flutter regex the
// login form uses (isFlutterEmail). Composes DS atoms — no raw hex.
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextField, Banner } from '@scripsteam/scrips-signal-ds';
import { AuthScreenShell, AuthLinkButton } from './auth-screen-shell';
import { isFlutterEmail } from './email-validation';
import { useForgotPassword } from './use-recovery';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const forgotPassword = useForgotPassword();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const emailValid = isFlutterEmail(email);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!emailValid) {
      setError('Enter a valid email address.');
      return;
    }
    // Account-enumeration guard: fire ForgotPassword but do NOT branch the UI on
    // its outcome. Unknown emails (and invited-but-passwordless accounts) return
    // a non-2xx from AccountController.ForgotPassword; surfacing that — staying on
    // the form with an error while known accounts navigate onward — is a public
    // oracle that reveals which emails have accounts. We advance to the code-entry
    // screen identically in every case (Flutter does no existence pre-check
    // either); a bad email simply fails later at code verification, revealing
    // nothing here.
    try {
      await forgotPassword.mutateAsync({ email });
    } catch {
      // Swallowed on purpose — the outcome must not be observable.
    }
    // Known backend follow-up (Codex, 2026-07-17), NOT fixable from the FE:
    // Identity's AccountController.ForgotPassword calls UpdateResetCodeToken
    // (which deletes any pending signup code for that email) BEFORE it checks
    // whether the account has a password hash at all, and only returns 401 for
    // the passwordless case afterward. So a passwordless invited provider who
    // mistakenly lands here and enters their email gets their signup code
    // silently deleted before the 401 comes back — they're left with neither
    // flow working. The FE cannot special-case this: telling "no password
    // set" apart from "unknown email" up front requires probing account
    // existence, which reintroduces the enumeration oracle this screen is
    // built to avoid (see the comment above). The fix belongs in Identity —
    // check for a null password hash and short-circuit BEFORE
    // UpdateResetCodeToken runs. Until that ships, invited providers must use
    // "Sign up with access code" from the sign-in screen, not this flow.
    //
    // Carry the email forward so the reset screen prefills it (Flutter passes the
    // email as the route argument to reset_password_access_code).
    navigate('/reset-password', { state: { email } });
  }

  return (
    <AuthScreenShell
      heading="Reset your password"
      intro="Enter the email tied to your account and we'll send you a reset code."
      footer={<AuthLinkButton onClick={() => navigate('/')}>Back to sign in</AuthLinkButton>}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {error && <Banner variant="error" role="alert">{error}</Banner>}
        {/* No type="email": the repo forbids HTML5 email validation (more
            permissive than Flutter's isFlutterEmail, and it intercepts submit
            before our validator runs — see email-validation.ts). */}
        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          disabled={forgotPassword.isPending}
        />
        <Button
          type="submit"
          variant="primary"
          loading={forgotPassword.isPending}
          disabled={forgotPassword.isPending}
        >
          Send reset code
        </Button>
      </form>
    </AuthScreenShell>
  );
}
